import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('click', this.#handleTabClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleTabClick);
  }

  /**
   * Switches between the Cart and Wishlist panels in the drawer.
   * @param {MouseEvent} event
   */
  #handleTabClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-cart-drawer-tab]') : null;
    if (!(target instanceof HTMLElement)) return;

    const tabName = target.dataset.cartDrawerTab;
    if (!tabName) return;

    const tabs = this.querySelectorAll('[data-cart-drawer-tab]');
    for (const tab of tabs) {
      tab.setAttribute('aria-selected', tab === target ? 'true' : 'false');
    }

    const panels = this.querySelectorAll('[data-cart-drawer-panel]');
    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.cartDrawerPanel !== tabName;
    }

    // Force <wishlist-page> (Swish) to re-init when the Wishlist tab is shown.
    // Swish's state subscription doesn't reliably re-render an element that
    // was first mounted inside a hidden panel, or after the wishlist is
    // mutated from elsewhere (PDP button, etc). Replacing the node with a
    // fresh instance triggers connectedCallback → state load each time.
    if (tabName === 'wishlist') {
      const oldEl = this.querySelector('wishlist-page');
      if (oldEl) {
        const newEl = document.createElement('wishlist-page');
        for (const attr of oldEl.attributes) {
          newEl.setAttribute(attr.name, attr.value);
        }
        oldEl.replaceWith(newEl);
      }
    }
  };

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    // Disable auto-open below the desktop breakpoint (lg = 1025px). On mobile
    // the drawer lives inside the hamburger <details> menu, which causes
    // showModal to freeze the body. The cart icon's count badge still updates
    // via the section morph, so users see the change and can open the cart
    // manually.
    if (window.matchMedia('(max-width: 1024px)').matches) return;

    const log = (/** @type {...unknown} */ ...args) =>
      console.log('[cart-drawer auto-open]', ...args);

    const isDrawerDisplayable = (/** @type {Element} */ el) => {
      let node = /** @type {Element | null} */ (el);
      while (node && node !== document.body) {
        if (node instanceof HTMLElement) {
          const style = getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden') {
            log('blocked by hidden ancestor:', node, 'display=', style.display, 'visibility=', style.visibility);
            return false;
          }
        }
        node = node.parentElement;
      }
      return true;
    };

    // Defer with rAF so any concurrent section morph (from cart-items-component
    // reacting to the same CartAddEvent) finishes before we re-query the live
    // drawer. Without this, `this` may already be detached.
    requestAnimationFrame(() => {
      const drawers = /** @type {NodeListOf<CartDrawerComponent>} */ (
        document.querySelectorAll('cart-drawer-component[auto-open]')
      );
      log('drawers found:', drawers.length);

      // Desktop path: a drawer is already visible.
      for (const drawer of drawers) {
        if (isDrawerDisplayable(drawer)) {
          log('visible drawer found, opening:', drawer);
          drawer.showDialog();
          return;
        }
      }

      // Mobile path: the drawer lives inside the hamburger <details>. Open the
      // details first, wait for the browser to render the now-visible subtree,
      // then re-query the live drawer (the section may have been morphed in
      // between) and open it.
      for (const drawer of drawers) {
        const details = /** @type {HTMLDetailsElement | null} */ (drawer.closest('details'));
        if (!details) {
          log('drawer has no <details> ancestor, skipping:', drawer);
          continue;
        }

        if (!details.open) {
          log('opening <details>', details);
          details.open = true;
        }

        // Wait a beat for the details + child layout to render before showModal.
        setTimeout(() => {
          // Re-query inside the details — the original `drawer` reference
          // may have been replaced by a header-section morph.
          const liveDrawer = /** @type {CartDrawerComponent | null} */ (
            details.querySelector('cart-drawer-component[auto-open]')
          );
          if (!liveDrawer) {
            log('no live drawer inside details after open');
            return;
          }
          if (!isDrawerDisplayable(liveDrawer)) {
            log('liveDrawer still not displayable, aborting');
            return;
          }
          log('opening liveDrawer', liveDrawer);
          liveDrawer.showDialog();
        }, 60);
        return;
      }

      log('no path matched — skipping silently');
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
